import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setReportingV2Filters } from '@/store/slices/reportingV2/reportingV2Slice';
import {
  type SavedReport,
  createSavedReport,
  deleteSavedReport,
  listSavedReports,
  migrateSavedFilters,
} from '@/store/slices/reportingV2/savedReports.api';
import { BookmarkBorderRounded, DeleteOutlineRounded, KeyboardArrowDownRounded } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { type MouseEvent, useCallback, useEffect, useState } from 'react';
import { COLORS } from '../theme';

/**
 * Save and reload a Unified Reporting view (FB-007).
 *
 * Saves the filter state only — date range, grouping and the id filters. That is
 * what makes a view reproducible; widget layout is fixed on this page, unlike
 * Custom Dashboards, so there is nothing else to preserve.
 */
/**
 * Adonis prefixes exception messages with their code, so the duplicate-name 400
 * arrives as "E_BAD_REQUEST: A saved report with that name already exists" and
 * was being shown to the user verbatim. Strip the code and keep the sentence.
 */
const readableApiError = (error: any, fallback: string): string => {
  const message = error?.response?.data?.message;
  if (typeof message !== 'string' || !message.trim()) return fallback;
  return message.replace(/^E_[A-Z0-9_]+:\s*/, '').trim() || fallback;
};

const LEGACY_FILTERS_NOTICE = 'Campaign/Line Item filters from this saved report were cleared — reselect them.';

const SavedReportsMenu = () => {
  const dispatch = useAppDispatch();
  const filters = useAppSelector(state => state.reportingV2.filters);

  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [reports, setReports] = useState<SavedReport[]>([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Rendered outside the Menu: loading a report closes it (#4614).
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setReports(await listSavedReports());
    } catch {
      // A failed list should not break the report itself; the menu just shows empty.
      setReports([]);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleLoad = (report: SavedReport) => {
    const saved = report.config?.filters;
    if (saved) {
      const { filters: migrated, clearedLegacy } = migrateSavedFilters(saved as Record<string, unknown>);
      dispatch(setReportingV2Filters(migrated));
      setNotice(clearedLegacy ? LEGACY_FILTERS_NOTICE : null);
    }
    setAnchor(null);
  };

  const handleDelete = async (event: MouseEvent, report: SavedReport) => {
    // Without this the row's onClick also fires and loads the report being deleted.
    event.stopPropagation();
    try {
      await deleteSavedReport(report.id);
      await refresh();
    } catch {
      // Save surfaces its failures; delete did not, so a failed delete left the
      // row on screen with no explanation and looked like the click missed.
      setError(`Could not delete "${report.name}"`);
    }
  };

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    try {
      await createSavedReport(trimmed, filters);
      setSaveOpen(false);
      setName('');
      await refresh();
    } catch (e: any) {
      // The API rejects a duplicate name per user; surface that rather than failing silently.
      setError(readableApiError(e, 'Could not save this report'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size='small'
        variant='outlined'
        startIcon={<BookmarkBorderRounded />}
        endIcon={<KeyboardArrowDownRounded />}
        onClick={event => {
          setError(null);
          setAnchor(event.currentTarget);
        }}
        sx={{ textTransform: 'none' }}
      >
        Saved reports
      </Button>

      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        <MenuItem
          onClick={() => {
            setAnchor(null);
            setSaveOpen(true);
          }}
        >
          Save current view…
        </MenuItem>
        <Divider />
        {error && !saveOpen ? (
          <MenuItem disabled>
            <Typography variant='body2' sx={{ color: 'error.main' }}>
              {error}
            </Typography>
          </MenuItem>
        ) : null}
        {reports.length === 0 ? (
          <MenuItem disabled>
            <Typography variant='body2' sx={{ color: COLORS.muted }}>
              No saved reports yet
            </Typography>
          </MenuItem>
        ) : (
          reports.map(report => (
            <MenuItem key={report.id} onClick={() => handleLoad(report)}>
              <ListItemText primary={report.name} />
              <IconButton
                size='small'
                aria-label={`Delete ${report.name}`}
                onClick={event => handleDelete(event, report)}
              >
                <DeleteOutlineRounded fontSize='small' />
              </IconButton>
            </MenuItem>
          ))
        )}
      </Menu>

      <Dialog open={saveOpen} onClose={() => setSaveOpen(false)} fullWidth maxWidth='xs'>
        <DialogTitle>Save current view</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              label='Name'
              value={name}
              onChange={event => setName(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter') void handleSave();
              }}
              error={!!error}
              helperText={error ?? 'Saves the current filters, date range and grouping.'}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Stack direction='row' spacing={1}>
            <Button onClick={() => setSaveOpen(false)}>Cancel</Button>
            <Button variant='contained' onClick={handleSave} disabled={busy || !name.trim()}>
              Save
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={!!notice}
        autoHideDuration={8000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity='info' onClose={() => setNotice(null)} sx={{ width: '100%' }}>
          {notice}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SavedReportsMenu;
